import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

const MobileBottomSheet = ({ isOpen, onClose, children }) => {
  const [dragY, setDragY] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const startY = useRef(0);
  const isDragging = useRef(false);
  const isFromHandle = useRef(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (isOpen && typeof document !== 'undefined') {
      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setDragY(0);
    }, 220);
  }, [onClose]);

  const handleTouchStart = (e, fromHandle = false) => {
    isFromHandle.current = fromHandle;
    const isAtTop = !contentRef.current || contentRef.current.scrollTop <= 0;
    if (fromHandle || isAtTop) {
      startY.current = e.touches[0].clientY;
      isDragging.current = true;
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    const isAtTop = !contentRef.current || contentRef.current.scrollTop <= 0;
    if (diff > 0 && (isFromHandle.current || isAtTop)) {
      setDragY(diff);
    } else if (diff < 0) {
      isDragging.current = false;
      setDragY(0);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const threshold = isFromHandle.current ? 70 : 180;
    if (dragY > threshold) {
      handleClose();
    } else {
      setDragY(0);
    }
    startY.current = 0;
    isFromHandle.current = false;
  };

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-xs transition-opacity duration-200 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={handleClose}
    >
      <div
        ref={contentRef}
        onTouchStart={(e) => handleTouchStart(e, false)}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => e.stopPropagation()}
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorY: 'contain',
          transform: isClosing
            ? 'translateY(100%)'
            : (dragY > 0 ? `translate3d(0, ${dragY}px, 0)` : undefined),
          transition: isDragging.current ? 'none' : 'transform 0.26s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
        className="w-full sm:max-w-md bg-white dark:bg-slate-900 rounded-t-[2.5rem] sm:rounded-3xl p-5 sm:p-6 space-y-4 max-h-[88vh] overflow-y-auto shadow-2xl pb-12 sm:pb-6 will-change-transform animate-in slide-in-from-bottom-8 duration-300"
      >
        {/* Handle táctil interactivo */}
        <div
          onTouchStart={(e) => handleTouchStart(e, true)}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ touchAction: 'none' }}
          className="flex justify-center pt-0 pb-3 -mt-2 -mx-4 cursor-grab active:cursor-grabbing select-none"
        >
          <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-700 rounded-full" />
        </div>

        {typeof children === 'function' ? children({ close: handleClose }) : children}
      </div>
    </div>,
    document.body
  );
};

export default MobileBottomSheet;
